import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import DotsHorizontalSvg from "@redotech/redo-icons/dots-horizontal.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { Flex } from "@redotech/redo-web/flex";
import { formatShortcut } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { Fragment, memo, useCallback, useState } from "react";
import { SupportTicketActionsDropdown } from "../actions/support-ticket-actions-dropdown";
import { useBulkSupportTicketAction } from "../hooks/use-bulk-support-ticket-action";
import { useSupportTicketActionToolbar } from "../hooks/use-support-ticket-action-toolbar";
import {
  MergeTicketModal,
  type MergeParams,
} from "./merge-ticket/merge-ticket-modal";

export interface SupportTicketActionToolbarProps {
  supportTicketId: SupportTicketId;
  supportTicket: SupportTicketListItemResponse;
  disabled?: boolean;
}

export const SupportTicketActionToolbar = memo(
  function SupportTicketActionToolbar({
    supportTicketId,
    supportTicket,
    disabled = false,
  }: SupportTicketActionToolbarProps) {
    const {
      primaryAction,
      supportTicket: actionContext,
      handlePrimaryAction,
    } = useSupportTicketActionToolbar(supportTicketId, supportTicket);
    const { startBulkSupportTicketAction } = useBulkSupportTicketAction();

    const [menuOpen, setMenuOpen] = useState(false);
    const [mergeOpen, setMergeOpen] = useState(false);

    const handleClose = useCallback(() => setMenuOpen(false), []);
    const handleOpenMergeModal = useCallback(() => setMergeOpen(true), []);
    const handleCloseMergeModal = useCallback(() => setMergeOpen(false), []);

    const handleMerge = useCallback(
      async (params: MergeParams) => {
        await startBulkSupportTicketAction({
          action: {
            _tag: "mergeSupportTickets",
            destinationSupportTicketId: params.primarySupportTicket.id,
            mongoCustomerId: params.resolvedCustomerId,
            mongoAssigneeId: params.assigneeId,
            status: params.status,
          },
          supportTicketIds: params.selectedSupportTickets.map(
            (selectedSupportTicket) => selectedSupportTicket.id,
          ),
        });
      },
      [startBulkSupportTicketAction],
    );

    const primaryEnabled = primaryAction.isEnabled?.(actionContext) ?? true;
    const primaryVisible = primaryAction.isVisible?.(actionContext) ?? true;

    return (
      <Fragment>
        <Flex align="center" gap="sm">
          <DropdownMenu onOpenChange={setMenuOpen} open={menuOpen}>
            <DropdownMenuTrigger asChild>
              <RedoButton
                disabled={disabled}
                hierarchy="secondary"
                IconLeading={DotsHorizontalSvg}
                size="sm"
              />
            </DropdownMenuTrigger>
            <SupportTicketActionsDropdown
              isOpen={menuOpen}
              onClose={handleClose}
              onMergeTicket={handleOpenMergeModal}
              supportTicket={supportTicket}
              supportTicketId={supportTicketId}
            />
          </DropdownMenu>
          {primaryVisible && (
            <Tooltip
              arrow
              title={
                primaryAction.shortcut
                  ? formatShortcut(primaryAction.shortcut)
                  : undefined
              }
            >
              <span>
                <RedoButton
                  disabled={disabled || !primaryEnabled}
                  hierarchy="primary"
                  IconLeading={primaryAction.Icon}
                  onClick={handlePrimaryAction}
                  size="sm"
                  text={primaryAction.title}
                />
              </span>
            </Tooltip>
          )}
        </Flex>
        <MergeTicketModal
          currentSupportTicket={supportTicket}
          onClose={handleCloseMergeModal}
          onMerge={handleMerge}
          open={mergeOpen}
        />
      </Fragment>
    );
  },
);
